/* ============================================================
   LEWD FASHION — "THE LEWK LIST" wishlist engine
   assets/lf-wishlist.js

   Native replacement for Swish / Wishlist King.
   Client-side only: localStorage + Shopify AJAX API.
   No dependencies. IIFE exposing ONE global: window.LFWishlist.

   Storage: localStorage["lf_lewk_list_v1"]
     { v:1, items:[{handle, addedAt}], rm:[{h, t}], updatedAt }
   rm = removal tombstones so deletes win when merging with the
   server copy (lf-lewk-sync — logged-in customers only).
   Handles only (stable + what /products/{handle}.js needs).
   Cap 100 items — oldest dropped first.

   Events: every mutation dispatches document-level
     CustomEvent "lf:wishlist:change"
     detail: { handle, action: add|remove|clear|sync, count }
   Cross-tab sync via the window "storage" event.

   Namespace: lfWish* / .lf-wish-* / --lf-wish-* ONLY.
   ============================================================ */
(function () {
  'use strict';

  var KEY = 'lf_lewk_list_v1';
  var CAP = 100;
  var RM_CAP = 120;
  var EVT = 'lf:wishlist:change';

  /* ---------------- storage ---------------- */

  var storageOk = (function () {
    try {
      var t = '__lf_wish_test__';
      window.localStorage.setItem(t, '1');
      window.localStorage.removeItem(t);
      return true;
    } catch (e) { return false; }
  })();

  var warned = false;
  function warnOnce() {
    if (warned) return;
    warned = true;
    toast('enable cookies to save your lewks ♡');
  }

  function blank() { return { v: 1, items: [], rm: [], updatedAt: 0 }; }

  function read() {
    if (!storageOk) return blank();
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return blank();
      var data = JSON.parse(raw);
      if (!data || data.v !== 1 || !Array.isArray(data.items)) return blank();
      if (!Array.isArray(data.rm)) data.rm = [];
      return data;
    } catch (e) { return blank(); }
  }

  function write(data) {
    if (!storageOk) { warnOnce(); return false; }
    try {
      data.updatedAt = Date.now();
      window.localStorage.setItem(KEY, JSON.stringify(data));
      return true;
    } catch (e) { warnOnce(); return false; }
  }

  function cleanHandle(handle) {
    /* LF product handles legitimately contain unicode (incl. emoji) —
       only strip characters that would break a /products/{handle}.js path */
    return String(handle || '').trim().toLowerCase().replace(/[\/\\?#%\s]/g, '');
  }

  /* ---------------- core API ---------------- */

  function has(handle) {
    handle = cleanHandle(handle);
    return read().items.some(function (it) { return it.handle === handle; });
  }

  function count() { return read().items.length; }

  function list() {
    /* newest first */
    return read().items.slice().sort(function (a, b) { return b.addedAt - a.addedAt; });
  }

  function add(handle) {
    handle = cleanHandle(handle);
    if (!handle) return false;
    var data = read();
    if (data.items.some(function (it) { return it.handle === handle; })) return true;
    data.rm = data.rm.filter(function (r) { return r.h !== handle; });
    data.items.push({ handle: handle, addedAt: Date.now() });
    /* hard cap: drop the oldest beyond CAP */
    if (data.items.length > CAP) {
      data.items.sort(function (a, b) { return a.addedAt - b.addedAt; });
      data.items = data.items.slice(data.items.length - CAP);
    }
    write(data);
    emit(handle, 'add');
    return true;
  }

  function remove(handle) {
    handle = cleanHandle(handle);
    var data = read();
    var before = data.items.length;
    data.items = data.items.filter(function (it) { return it.handle !== handle; });
    if (data.items.length === before) return false;
    data.rm = data.rm.filter(function (r) { return r.h !== handle; });
    data.rm.push({ h: handle, t: Date.now() });
    if (data.rm.length > RM_CAP) data.rm = data.rm.slice(data.rm.length - RM_CAP);
    write(data);
    emit(handle, 'remove');
    return true;
  }

  function toggle(handle) {
    if (has(handle)) { remove(handle); return false; }
    add(handle);
    return true;
  }

  function clear() {
    var data = read();
    var now = Date.now();
    var rm = data.rm.filter(function (r) {
      return !data.items.some(function (it) { return it.handle === r.h; });
    });
    data.items.forEach(function (it) { rm.push({ h: it.handle, t: now }); });
    var fresh = blank();
    fresh.rm = rm.slice(-RM_CAP);
    write(fresh);
    emit(null, 'clear');
  }

  /* ---------------- events + DOM sync ---------------- */

  function emit(handle, action) {
    syncAll();
    try {
      document.dispatchEvent(new CustomEvent(EVT, {
        detail: { handle: handle, action: action, count: count() }
      }));
    } catch (e) { /* ancient browsers: state still saved */ }
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function syncEl(el, active) {
    el.classList.toggle('lf-wish-active', active);
    el.setAttribute('aria-pressed', active ? 'true' : 'false');
    var title = el.getAttribute('data-lf-wish-title') || 'this';
    el.setAttribute('aria-label', active
      ? 'Remove ' + title + ' from your Lewk List'
      : 'Save ' + title + ' to your Lewk List');
  }

  function syncAll() {
    var n = count();
    var els = document.querySelectorAll('[data-lf-wish-handle]');
    for (var i = 0; i < els.length; i++) {
      syncEl(els[i], has(els[i].getAttribute('data-lf-wish-handle')));
    }
    var badges = document.querySelectorAll('[data-lf-wish-count]');
    for (var j = 0; j < badges.length; j++) {
      var b = badges[j];
      var prev = parseInt(b.getAttribute('data-lf-wish-prev') || '0', 10);
      b.textContent = n > 99 ? '99+' : String(n);
      b.hidden = n === 0;
      b.style.display = n === 0 ? 'none' : '';
      if (n > prev && !reducedMotion()) {
        b.classList.remove('lf-wish-badge-pop');
        void b.offsetWidth; /* restart animation */
        b.classList.add('lf-wish-badge-pop');
      }
      b.setAttribute('data-lf-wish-prev', String(n));
    }
  }

  /* ---------------- sparkles (add micro-interaction) ---------------- */

  function sparkle(el) {
    if (reducedMotion()) return;
    var old = el.querySelector('.lf-wish-sparks');
    if (old) old.remove();
    var wrap = document.createElement('span');
    wrap.className = 'lf-wish-sparks';
    wrap.setAttribute('aria-hidden', 'true');
    var glyphs = ['✦', '✧', '✦', '✧']; /* ✦ ✧ ✦ ✧ */
    for (var i = 0; i < 4; i++) {
      var s = document.createElement('span');
      s.className = 'lf-wish-spark lf-wish-spark--' + (i + 1);
      s.textContent = glyphs[i];
      wrap.appendChild(s);
    }
    el.appendChild(wrap);
    setTimeout(function () { wrap.remove(); }, 700);
  }

  function pop(el) {
    if (reducedMotion()) return;
    el.classList.remove('lf-wish-pop');
    void el.offsetWidth;
    el.classList.add('lf-wish-pop');
  }

  /* ---------------- toast ---------------- */

  var toastTimer = null;
  function toast(msg) {
    var el = document.getElementById('lf-wish-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'lf-wish-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('lf-wish-toast-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('lf-wish-toast-on');
    }, 2600);
  }

  /* ---------------- interaction (delegated) ---------------- */

  function activate(el) {
    var handle = el.getAttribute('data-lf-wish-handle');
    var nowActive = toggle(handle);
    if (nowActive) { pop(el); sparkle(el); }
  }

  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('[data-lf-wish-handle]');
    if (!el) return;
    /* chips can sit inside product-card anchors — never navigate */
    e.preventDefault();
    e.stopPropagation();
    activate(el);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var el = e.target.closest && e.target.closest('[data-lf-wish-handle]');
    if (!el || el.tagName === 'BUTTON') return; /* real buttons fire click natively */
    e.preventDefault();
    activate(el);
  }, true);

  /* cross-tab sync */
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    syncAll();
    try {
      document.dispatchEvent(new CustomEvent(EVT, {
        detail: { handle: null, action: 'sync', count: count() }
      }));
    } catch (err) { /* noop */ }
  });

  /* late-rendered cards (quick views, drawers, related products) */
  function observe() {
    if (!window.MutationObserver) return;
    var pending = null;
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute && node.hasAttribute('data-lf-wish-handle') ||
              node.querySelector && node.querySelector('[data-lf-wish-handle]')) {
            clearTimeout(pending);
            pending = setTimeout(syncAll, 60);
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function injectGradient() {
    /* ONE document-level gradient def all heart fills reference.
       Per-button defs break when the same product renders twice and the
       first id match sits in a display:none block (PDP mobile/desktop
       title rows) — Safari then paints the fill white. This wrapper svg
       is zero-size but NOT display:none, so url(#lfWishGrad) always
       resolves to a paintable server. */
    if (document.getElementById('lfWishGrad')) return;
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.style.position = 'absolute';
    var defs = document.createElementNS(ns, 'defs');
    var grad = document.createElementNS(ns, 'linearGradient');
    grad.setAttribute('id', 'lfWishGrad');
    grad.setAttribute('x1', '0'); grad.setAttribute('y1', '0');
    grad.setAttribute('x2', '1'); grad.setAttribute('y2', '1');
    var s1 = document.createElementNS(ns, 'stop');
    s1.setAttribute('offset', '0%'); s1.setAttribute('stop-color', '#ff97ad');
    var s2 = document.createElementNS(ns, 'stop');
    s2.setAttribute('offset', '100%'); s2.setAttribute('stop-color', '#e8457a');
    grad.appendChild(s1); grad.appendChild(s2);
    defs.appendChild(grad);
    svg.appendChild(defs);
    document.body.appendChild(svg);
  }

  function init() {
    injectGradient();
    syncAll();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------------- server sync support ---------------- */

  function doc() {
    var d = read();
    return { items: d.items, rm: d.rm };
  }

  function merge(remote) {
    /* CRDT-lite union with tombstones — mirrors the lf-lewk-sync server:
       newest add per handle wins unless an equal-or-newer tombstone exists.
       Accepts server shape {h,t} or local shape {handle,addedAt}. */
    remote = remote || {};
    var local = read();
    var rmT = {}, addT = {};
    [local.rm, Array.isArray(remote.rm) ? remote.rm : []].forEach(function (arr) {
      arr.forEach(function (r) {
        var h = cleanHandle(r.h || r.handle), t = Number(r.t) || 0;
        if (h && (!rmT[h] || t > rmT[h])) rmT[h] = t;
      });
    });
    [local.items, Array.isArray(remote.items) ? remote.items : []].forEach(function (arr) {
      arr.forEach(function (it) {
        var h = cleanHandle(it.h || it.handle), t = Number(it.t || it.addedAt) || 0;
        if (h && (!addT[h] || t > addT[h])) addT[h] = t;
      });
    });
    var items = [];
    Object.keys(addT).forEach(function (h) {
      if (rmT[h] && rmT[h] >= addT[h]) return;
      items.push({ handle: h, addedAt: addT[h] });
    });
    items.sort(function (a, b) { return b.addedAt - a.addedAt; });
    var before = JSON.stringify(local.items.slice().sort(function (a, b) {
      return b.addedAt - a.addedAt;
    }));
    var changed = JSON.stringify(items) !== before;
    local.items = items.slice(0, CAP);
    local.rm = Object.keys(rmT).map(function (h) { return { h: h, t: rmT[h] }; })
      .sort(function (a, b) { return b.t - a.t; }).slice(0, RM_CAP);
    write(local);
    if (changed) emit(null, 'sync'); else syncAll();
    return changed;
  }

  /* ---------------- public API ---------------- */

  window.LFWishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    list: list,
    count: count,
    clear: clear,
    toast: toast,
    sync: syncAll,
    merge: merge,
    doc: doc,
    storageOk: function () { return storageOk; }
  };
})();
